import { isProbabilistic } from "./probabilisticGrading";

// Scoring is in marks, never in questions.
//
// Counting questions treats a 1-mark multiple choice and a 3-mark extended response as
// the same achievement, and it throws partial credit away entirely: a student who earns
// 2 of 3 marks scores the same zero as one who wrote nothing. Both realms — the module
// quiz and the practice quizzes — score through this file so a grade means the same
// thing wherever it appears.

// Questions authored before max_marks existed have no value set; they are worth 1.
export const maxMarksFor = (question) => {
  const declared = Number(question?.max_marks);
  return Number.isFinite(declared) && declared > 0 ? declared : 1;
};

// A question is scorable once its result is known. An open-ended answer still being
// marked — or one we failed to mark — has no verdict, and scoring it as zero would
// report our own pending work as the student's failure.
export const isScorable = (question, verdict) =>
  isProbabilistic(question) ? verdict?.state === "graded" : true;

// Did the student actually attempt this? Blank strings, empty arrays and untouched
// blanks are all "no". A partly-filled fill-in-the-blank counts as an attempt.
export const isAnswered = (value) => {
  if (value == null) return false;
  if (Array.isArray(value)) return value.some((entry) => isAnswered(entry));

  return String(value).trim() !== "";
};

// `correct` covers deterministic questions (all-or-nothing); `verdict` carries the
// awarded marks for open-ended ones.
export const marksEarnedFor = (question, { correct, verdict } = {}) => {
  const possible = maxMarksFor(question);

  if (isProbabilistic(question)) {
    const awarded = Number(verdict?.marksAwarded);
    if (!Number.isFinite(awarded)) return 0;
    return Math.min(Math.max(awarded, 0), possible);
  }

  return correct ? possible : 0;
};

/**
 * Score a set of questions in marks.
 *
 * A skipped or unanswered question is left out of BOTH halves of the fraction — you
 * cannot be scored on work you did not attempt, and it keeps this in step with the
 * backend accuracy stat, which has always excluded skips. Callers pass `answered: false`
 * for those; omitting the flag treats the question as attempted.
 *
 * @param {Array} entries  [{ question, answered, correct, verdict }]
 * @returns {{earned:number, possible:number, percent:number, pending:number, skipped:number}}
 *          `pending` counts questions still awaiting a mark — while it is above zero the
 *          percentage is provisional. `skipped` is the count left out, for context the
 *          UI can show next to the score.
 */
export const scoreQuiz = (entries) => {
  let earned = 0;
  let possible = 0;
  let pending = 0;
  let skipped = 0;

  (entries ?? []).forEach(({ question, answered = true, correct, verdict }) => {
    if (!question) return;

    // Checked before the pending test: an unattempted open-ended question has no verdict
    // coming, and counting it as pending would leave the score unsettled forever.
    if (!answered) {
      skipped += 1;
      return;
    }

    if (!isScorable(question, verdict)) {
      pending += 1;
      return;
    }

    earned += marksEarnedFor(question, { correct, verdict });
    possible += maxMarksFor(question);
  });

  return {
    earned,
    possible,
    percent: possible > 0 ? (earned / possible) * 100 : 0,
    pending,
    skipped,
  };
};

// Marks can be fractional in principle; show a decimal only when there is one.
export const formatMarks = (marks) =>
  Number.isInteger(marks) ? String(marks) : marks.toFixed(1);

export const formatPercent = (percent) => `${percent.toFixed(1)}%`;
